import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ILegalEntity } from '@cemex-core/cmx-api';

import {
  SelectJobsite,
  SelectPod,
  SelectProductLines,
  SetActiveProductLines,
  SetJobsiteFilter,
  SetSelectingJobsite,
} from '../store/actions';
import { GlobalFiltersPersistenceService } from './global-filters-persistence.service';
import { GlobalFiltersJobsitesService } from './global-filters-jobsites.service';
import { GlobalFiltersProductLinesService } from './global-filters-product-lines.service';
import { GlobalFilterPersistence, GlobalFiltersState } from '../models';
import { BehaviorSubject } from 'rxjs';
import { GlobalFiltersPodsService } from './global-filters-pods.service';

@Injectable({
  providedIn: 'root',
})
export class GlobalFiltersService {
  private shouldOpenJobsiteModalSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private productLines: GlobalFiltersProductLinesService,
    private store: Store<GlobalFiltersState>,
    private persistence: GlobalFiltersPersistenceService,
    private jobsites: GlobalFiltersJobsitesService,
    private pods: GlobalFiltersPodsService,
  ) {}

  setDefaultFiltersCalendarView(legalEntity: ILegalEntity, jobsiteId: number, productLinesIds: number[]) {
    this.jobsites.getJobsites(legalEntity.legalEntityId);
    this.productLines.getActiveProductLines(legalEntity.legalEntityId, legalEntity.countryCode, jobsiteId);
    this.productLines.getProductLines(legalEntity.legalEntityId, legalEntity.countryCode);
    this.selectJobsite(legalEntity, jobsiteId, false);
    this.selectPod(legalEntity, null);
    this.selectProductLines(legalEntity.legalEntityId, productLinesIds);
  }

  setFilters(legalEntity: ILegalEntity) {
    const legalEntityId = legalEntity.legalEntityId;
    const country = legalEntity.countryCode;
    this.store.dispatch(new SetJobsiteFilter(''));
    const filters: GlobalFilterPersistence = this.persistence.getCustomerFiltersPersistence(legalEntityId);
    if (!filters) {
      this.getDataAndEmptySelect(legalEntity);
      this.shouldOpenJobsiteModalSubject.next(true);
      return;
    }
    // Get Data
    this.jobsites.getJobsites(legalEntityId);
    this.productLines.getProductLines(legalEntityId, country);

    // Select based on persistence
    if (filters.selectedJobsiteId) {
      const resetProductLines: boolean =
        filters.selectedProductLinesIds && filters.selectedProductLinesIds.length === 0;
      this.selectJobsite(legalEntity, filters.selectedJobsiteId, resetProductLines, false);
    } else {
      this.selectJobsite(legalEntity, null, true, false);
      this.shouldOpenJobsiteModalSubject.next(true);
    }

    if (filters.selectedProductLinesIds) {
      this.selectProductLines(legalEntityId, filters.selectedProductLinesIds);
    } else {
      this.resetProductLines(legalEntity, filters.selectedJobsiteId);
    }
    // In case product lines are in the storage, set active product lines in the dropdown in global filters
    if (filters.activeProductLinesIds && filters.activeProductLinesIds[filters.selectedJobsiteId]) {
      this.store.dispatch(
        new SetActiveProductLines(filters.selectedJobsiteId, filters.activeProductLinesIds[filters.selectedJobsiteId]),
      );
    }
  }

  selectProductLines(legalEntityId: number, productLinesIds: number[]) {
    this.persistence.saveProductLinesSelection(legalEntityId, productLinesIds);
    this.store.dispatch(new SelectProductLines(legalEntityId, productLinesIds));
  }

  async selectJobsite(legalEntity: ILegalEntity, jobsiteId: number, resetProductLines = true, calendarView = true) {
    const legalEntityId = legalEntity.legalEntityId;
    this.store.dispatch(new SelectJobsite(legalEntityId, jobsiteId));
    this.persistence.saveJobsiteSelection(legalEntityId, jobsiteId);
    this.shouldOpenJobsiteModalSubject.next(false);
    if (resetProductLines) {
      await this.resetProductLines(legalEntity, jobsiteId);
    }
    // ! If component no longer emit action on programatic changes, this should be removed
    this.store.dispatch(new SetSelectingJobsite(false));
    if (!!calendarView && jobsiteId !== null) {
      await this.pods.getPods(legalEntityId, jobsiteId);
    }
  }

  async selectPod(legalEntity: ILegalEntity, podId: number) {
    const legalEntityId = legalEntity.legalEntityId;
    this.store.dispatch(new SelectPod(legalEntityId, podId));
  }

  /**
   * Set all enabled product lines as selected
   * @param legalEntity
   * @param jobsiteId
   */
  async resetProductLines(legalEntity: ILegalEntity, jobsiteId: number) {
    const country = legalEntity.countryCode;
    try {
      const activeProductLinesIds = await this.productLines.getActiveProductLines(
        legalEntity.legalEntityId,
        country,
        jobsiteId,
      );
      await this.selectProductLines(legalEntity.legalEntityId, activeProductLinesIds);
    } catch (error) {
      console.log(error);
    }
  }

  get shouldOpenJobsiteModal() {
    return this.shouldOpenJobsiteModalSubject.asObservable();
  }

  private getDataAndEmptySelect(legalEntity: ILegalEntity) {
    const legalEntityId = legalEntity.legalEntityId;
    const country = legalEntity.countryCode;
    this.jobsites.getJobsites(legalEntityId);
    this.productLines.getProductLines(legalEntityId, country);
    this.selectJobsite(legalEntity, null, false);
    this.selectProductLines(legalEntityId, []);
  }
}
